export const DEFAULT_INPUT = {
  currentAge: 35,
  sipEndAge: 50,
  sipAmount: 20000,
  returnRate: 12,
  stepUp: 7,
  lumpsumAmount: 10000,
  swpStartAge: 55,
  monthlyWithdrawal: 60000,
  yearlyIncrease: 12,
  withdrawReturnRate: 10
};

export const INPUT_LIMITS = {
  currentAge: {min: 18, max: 100, step: 1},
  sipEndAge: {min: 18, max: 100, step: 1},
  sipAmount: {min: 1000, max: 200000, step: 1000},
  returnRate: {min: 1, max: 12, step: 0.5},
  stepUp: {min: 0, max: 100, step: 0.5},
  lumpsumAmount: {min: 10000, max: 2000000, step: 10000},
  swpStartAge: {min: 18, max: 100, step: 1},
  monthlyWithdrawal: {min: 10000, max: 5000000, step: 5000},
  yearlyIncrease: {min: 0, max: 100, step: 0.5},
  withdrawReturnRate: {min: 1, max: 12, step: 0.5}
};

export const MAX_SIMULATION_AGE = 100;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const roundToStep = (value, step) => Math.round(value / step) * step;

const normalizeInput = (input) => ({
  currentAge: clamp(
    Math.round(input.currentAge),
    INPUT_LIMITS.currentAge.min,
    INPUT_LIMITS.currentAge.max
  ),
  sipEndAge: clamp(
    Math.round(input.sipEndAge),
    INPUT_LIMITS.sipEndAge.min,
    INPUT_LIMITS.sipEndAge.max
  ),
  sipAmount: clamp(
    roundToStep(input.sipAmount, INPUT_LIMITS.sipAmount.step),
    INPUT_LIMITS.sipAmount.min,
    INPUT_LIMITS.sipAmount.max
  ),
  returnRate: clamp(
    roundToStep(input.returnRate, INPUT_LIMITS.returnRate.step),
    INPUT_LIMITS.returnRate.min,
    INPUT_LIMITS.returnRate.max
  ),
  stepUp: clamp(
    roundToStep(input.stepUp, INPUT_LIMITS.stepUp.step),
    INPUT_LIMITS.stepUp.min,
    INPUT_LIMITS.stepUp.max
  ),
  lumpsumAmount: clamp(
    roundToStep(input.lumpsumAmount, INPUT_LIMITS.lumpsumAmount.step),
    INPUT_LIMITS.lumpsumAmount.min,
    INPUT_LIMITS.lumpsumAmount.max
  ),
  swpStartAge: clamp(
    Math.round(input.swpStartAge),
    INPUT_LIMITS.swpStartAge.min,
    INPUT_LIMITS.swpStartAge.max
  ),
  monthlyWithdrawal: clamp(
    roundToStep(input.monthlyWithdrawal, INPUT_LIMITS.monthlyWithdrawal.step),
    INPUT_LIMITS.monthlyWithdrawal.min,
    INPUT_LIMITS.monthlyWithdrawal.max
  ),
  yearlyIncrease: clamp(
    roundToStep(input.yearlyIncrease, INPUT_LIMITS.yearlyIncrease.step),
    INPUT_LIMITS.yearlyIncrease.min,
    INPUT_LIMITS.yearlyIncrease.max
  ),
  withdrawReturnRate: clamp(
    roundToStep(input.withdrawReturnRate, INPUT_LIMITS.withdrawReturnRate.step),
    INPUT_LIMITS.withdrawReturnRate.min,
    INPUT_LIMITS.withdrawReturnRate.max
  )
});

export const getSipSwpReturns = (investmentData) => {
  const currentAge = Number(investmentData.currentAge);
  const sipEndAge = Number(investmentData.sipEndAge);
  const sipAmount = Number(investmentData.sipAmount);
  const returnRate = Number(investmentData.returnRate);
  const stepUp = Number(investmentData.stepUp);
  const lumpsumAmount = Number(investmentData.lumpsumAmount);
  const swpStartAge = Number(investmentData.swpStartAge);
  const monthlyWithdrawal = Number(investmentData.monthlyWithdrawal);
  const yearlyIncrease = Number(investmentData.yearlyIncrease);
  const withdrawReturnRate = Number(investmentData.withdrawReturnRate);
  const maxAge = MAX_SIMULATION_AGE;

  let corpus = lumpsumAmount;
  let totalInvested = lumpsumAmount;
  let withdrawal = monthlyWithdrawal;
  const yearlyData = [];
  let corpusAtSipEnd = null;
  let corpusBeforeSwp = null;
  let corpusUsed = 0;
  let leftoverCorpus = 0;
  let sustainTillAge = MAX_SIMULATION_AGE;

  for (let age = currentAge; age <= maxAge; age++) {
    const isSip = age <= sipEndAge;
    const isSwp = age >= swpStartAge;
    const thisYearSip = isSip
      ? sipAmount * Math.pow(1 + stepUp / 100, age - currentAge)
      : 0;
    let yearInvested = 0;
    let yearWithdrawn = 0;
    const yearStartCorpus = corpus;

    for (let m = 0; m < 12; m++) {
      if (isSip) {
        corpus += thisYearSip;
        yearInvested += thisYearSip;
      }

      corpus *= Math.pow(
        1 + (isSwp ? withdrawReturnRate : returnRate) / 100,
        1 / 12
      );
    }

    if (isSwp) {
      const yearlyWithdrawal = withdrawal * 12;

      if (corpus < yearlyWithdrawal) {
        yearWithdrawn = corpus;
        leftoverCorpus = yearStartCorpus;
        corpus = 0;
        sustainTillAge = age - 1;
        break;
      }

      corpus -= yearlyWithdrawal;
      yearWithdrawn = yearlyWithdrawal;
      corpusUsed += yearlyWithdrawal;
      leftoverCorpus = corpus;
    }

    totalInvested += yearInvested;
    yearlyData.push({
      year: age - currentAge + 1,
      yearLabel: `${age}`,
      yearStartCorpus: Math.round(yearStartCorpus),
      Investment: Math.round(totalInvested),
      Gain: Math.round(yearStartCorpus) - Math.round(totalInvested),
      Corpus: Math.round(corpus),
      yearlyWithdrawal: isSwp ? Math.round(yearWithdrawn) : 0,
      monthlySip: Math.round(thisYearSip),
      yearlySip: Math.round(thisYearSip * 12),
      monthlyWithdrawal: isSwp ? Math.round(withdrawal) : 0,
      phase:
        isSip && isSwp
          ? "SIP+SWP"
          : isSip
            ? "SIP"
            : isSwp
              ? "SWP"
              : "HOLD"
    });

    if (age === sipEndAge) {
      corpusAtSipEnd = corpus;
    }

    if (age === swpStartAge - 1) {
      corpusBeforeSwp = corpus;
    }

    if (corpus === 0) {
      break;
    }

    if (isSwp) {
      withdrawal *= 1 + yearlyIncrease / 100;
    }
  }

  if (corpusAtSipEnd === null) {
    corpusAtSipEnd = corpus;
  }

  if (corpusBeforeSwp === null) {
    corpusBeforeSwp = corpus;
  }

  return {
    returnData: yearlyData,
    sustainTillAge,
    leftoverCorpus: Math.round(leftoverCorpus),
    corpusBeforeSwp: Math.round(corpusBeforeSwp),
    corpusUsed: Math.round(corpusUsed),
    corpusAtSipEnd: Math.round(corpusAtSipEnd)
  };
};

export const calculateSipSwp = (input) => getSipSwpReturns(normalizeInput(input));

export const formatCurrency = (value) =>
  `₹${Math.round(value).toLocaleString("en-IN")}`;

export const formatCompactCurrency = (value) => {
  const absValue = Math.abs(value);

  if (absValue >= 10000000) {
    return `₹${(value / 10000000).toFixed(1)}Cr`;
  }

  if (absValue >= 100000) {
    return `₹${(value / 100000).toFixed(1)}L`;
  }

  if (absValue >= 1000) {
    return `₹${(value / 1000).toFixed(0)}k`;
  }

  return formatCurrency(value);
};

export const formatSustainableAge = (age) => {
  if (age >= MAX_SIMULATION_AGE) {
    return "99+";
  }

  return String(age);
};

export const snapToStep = (value, step) => roundToStep(value, step);
